import { useEffect, useState } from "react";
import { Wallet, CalendarClock, History } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtMYT } from "@/lib/myt";

const shortId = (id) => (id || "").slice(-6);

const STATUS_STYLE = {
  ACTIVE: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10",
  PENDING: "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/10",
  COMPLETED: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10",
  PAID: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10",
  REJECTED: "text-red-400 border-red-400/30 bg-red-400/10",
  CANCELLED: "text-[#94A3B8] border-[#222530] bg-[#222530]/40",
  RECORDED: "text-sky-400 border-sky-400/30 bg-sky-400/10",
};

function StatusChip({ status }) {
  return (
    <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${STATUS_STYLE[status] || "text-[#94A3B8] border-[#222530]"}`}>
      {status || "—"}
    </span>
  );
}

export default function AdminWallet() {
  const [period, setPeriod] = useState("current"); // default view: Current Period
  const [wallet, setWallet] = useState(null); // period-filtered view (cards + transactions)
  const [trueWallet, setTrueWallet] = useState(null); // all-time — drives real withdrawal balance
  const [withdrawals, setWithdrawals] = useState([]);
  const [showAdminWithdraw, setShowAdminWithdraw] = useState(false);
  const [adminWithdrawAmount, setAdminWithdrawAmount] = useState("");
  const [adminWithdrawNote, setAdminWithdrawNote] = useState("");
  const [rejectWd, setRejectWd] = useState(null);
  const [rejectWdReason, setRejectWdReason] = useState("");
  const [showStartPeriod, setShowStartPeriod] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/admin/wallet?period=${period}`).then((res) => setWallet(res.data)).catch(() => {});
    api.get(`/admin/wallet?period=historical`).then((res) => setTrueWallet(res.data)).catch(() => {});
    api.get("/admin/withdrawals").then((res) => setWithdrawals(res.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const submitStartPeriod = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/wallet/period/start", { confirm: true });
      toast.success(`New accounting period started · ${fmtMYT(res.data.cutoff)}`);
      setShowStartPeriod(false);
      setPeriod("current");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  const submitAdminWithdraw = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/wallet/withdraw", { amount: Number(adminWithdrawAmount), note: adminWithdrawNote });
      toast.success(res.data.message);
      setShowAdminWithdraw(false);
      setAdminWithdrawAmount("");
      setAdminWithdrawNote("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  const completeWithdrawal = async (w) => {
    if (!window.confirm(`Mark ${w.ib_name}'s $${w.amount?.toFixed(2)} withdrawal as COMPLETED? Only do this after you have manually transferred the money outside the system.`)) return;
    try {
      await api.post(`/admin/withdrawals/${w.id}/complete`);
      toast.success("Withdrawal marked as completed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitRejectWithdrawal = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/withdrawals/${rejectWd.id}/reject`, { reason: rejectWdReason });
      toast.success("Withdrawal rejected — funds released back to the IB");
      setRejectWd(null);
      setRejectWdReason("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  const inputCls = "input-lux";
  const labelCls = "block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide";

  const meta = wallet?.period_meta;
  const currentUnconfigured = period === "current" && wallet && meta && !meta.configured;
  const hasTotals = wallet && wallet.available_balance !== undefined && wallet.available_balance !== null;
  const trueBalance = trueWallet?.available_balance ?? 0;

  const toggleBtn = (key, label, Icon) => (
    <button
      onClick={() => setPeriod(key)}
      className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-3.5 py-1.5 border transition-colors flex items-center gap-1.5 ${
        period === key ? "text-[#D4AF37] border-[#D4AF37]/50 bg-[#D4AF37]/10" : "text-[#64748B] border-[#222530] hover:border-[#D4AF37]/30"
      }`}
      data-testid={`period-toggle-${key}`}
    >
      <Icon className="h-3 w-3" /> {label}
    </button>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="admin-wallet-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow-label">Administration</p>
          <h1 className="page-title mt-2">Wallet & Withdrawals</h1>
          <p className="page-sub mt-1">Internal wallet ledger (admin allocation history) and IB withdrawal processing — bank transfers happen manually outside the system.</p>
        </div>
        <button
          onClick={() => setShowStartPeriod(true)}
          className="btn-gold-outline px-4 py-2.5 text-xs flex items-center gap-2 shrink-0"
          data-testid="start-period-button"
        >
          <CalendarClock className="h-3.5 w-3.5" /> Start New Accounting Period
        </button>
      </div>

      {/* Period toggle + banner */}
      <div className="space-y-3">
        <div className="flex gap-2 flex-wrap" data-testid="period-toggle">
          {toggleBtn("current", "Current Period", CalendarClock)}
          {toggleBtn("historical", "Historical", History)}
        </div>
        {period === "current" && meta?.configured && (
          <div className="card-lux px-5 py-3.5 flex items-center gap-3 flex-wrap" data-testid="period-banner">
            <span className="text-[10px] font-mono tracking-widest uppercase text-[#D4AF37]">Current Accounting Period</span>
            <span className="text-xs text-[#94A3B8]">
              Started <span className="text-white font-mono" data-testid="period-started-at">{fmtMYT(meta.cutoff)}</span>
              {meta.started_by_name ? <span className="text-[#64748B]"> · by {meta.started_by_name}</span> : null}
            </span>
          </div>
        )}
        {period === "historical" && (
          <div className="card-lux px-5 py-3.5" data-testid="period-banner">
            <span className="text-[10px] font-mono tracking-widest uppercase text-[#94A3B8]">Historical · all-time totals since inception (unchanged records)</span>
          </div>
        )}
      </div>

      <div className="space-y-6" data-testid="wallet-section">
        {currentUnconfigured ? (
          <div className="card-lux p-8 text-center space-y-2" data-testid="period-unconfigured-notice">
            <p className="text-sm text-white font-medium">No accounting period started yet</p>
            <p className="text-xs text-[#64748B] max-w-md mx-auto">
              Click <span className="text-[#D4AF37]">“Start New Accounting Period”</span> above to begin fresh from now. Meanwhile, switch to <span className="text-[#D4AF37]">Historical</span> to view all-time totals. No financial records are ever deleted or changed.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
              {[
                ["Available Balance", wallet?.available_balance, "wallet-balance", null],
                ["Total Revenue", wallet?.total_revenue, "wallet-revenue", null],
                ["Admin Allocation (80%)", wallet?.total_admin_allocation, "wallet-admin-allocation", null],
                ["IB Commissions (20%)", wallet?.ib_commission_available, "wallet-ib-commission", wallet?.total_ib_commission != null ? `earned $${wallet.total_ib_commission.toFixed(2)}` : null],
                ["Total Withdrawn", wallet?.total_withdrawn, "wallet-withdrawn", null],
                ["Live & Learn — 80% Allocation", wallet?.live_learn_ib_allocation, "wallet-ll-allocation", wallet?.live_learn_admin_allocation != null ? `admin 20% $${wallet.live_learn_admin_allocation.toFixed(2)}` : null],
              ].map(([label, val, testid, sub]) => (
                <div key={label} className="card-lux p-5">
                  <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">{label}</p>
                  <p className="text-2xl font-bold text-[#D4AF37] mt-1.5 font-mono" data-testid={testid}>
                    {val === undefined || val === null ? "—" : `$${val.toFixed(2)}`}
                  </p>
                  {sub && <p className="text-[10px] text-[#64748B] font-mono mt-1" data-testid={`${testid}-earned`}>{sub}</p>}
                </div>
              ))}
            </div>

            <div className="card-lux overflow-hidden">
              <p className="px-5 pt-5 text-[10px] font-mono tracking-widest uppercase text-[#64748B]">
                Wallet Transactions {period === "current" ? "(current period)" : "(all-time)"}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm mt-2" data-testid="wallet-tx-table">
                  <thead>
                    <tr className="border-b border-[#222530] text-left">
                      <th className="px-5 py-3 text-xs font-medium text-[#64748B] tracking-wide">Date</th>
                      <th className="px-5 py-3 text-xs font-medium text-[#64748B] tracking-wide">Client</th>
                      <th className="px-5 py-3 text-xs font-medium text-[#64748B] tracking-wide">Reference</th>
                      <th className="px-5 py-3 text-xs font-medium text-[#64748B] tracking-wide">Status</th>
                      <th className="px-5 py-3 text-xs font-medium text-[#64748B] tracking-wide text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#222530]">
                    {!wallet?.transactions || wallet.transactions.length === 0 ? (
                      <tr><td colSpan={5} className="px-5 py-8 text-center text-[#64748B]" data-testid="wallet-tx-empty">No wallet transactions {period === "current" ? "in this period" : "yet"}.</td></tr>
                    ) : (
                      wallet.transactions.map((tx) => (
                        <tr key={tx.id} data-testid={`wallet-tx-row-${shortId(tx.id)}`}>
                          <td className="px-5 py-3 text-[#94A3B8] text-xs font-mono">{fmtMYT(tx.created_at)}</td>
                          <td className="px-5 py-3 text-xs text-white">
                            {tx.kind === "admin_withdrawal" ? <span className="text-[#64748B]">—</span> : <>{tx.client_name} <span className="text-[#64748B] font-mono text-[10px]">{tx.client_uid}</span></>}
                          </td>
                          <td className="px-5 py-3 text-xs text-[#94A3B8]">{tx.reference}</td>
                          <td className="px-5 py-3"><StatusChip status={(tx.status || "").toUpperCase()} /></td>
                          <td className={`px-5 py-3 text-right text-xs font-mono ${tx.amount < 0 ? "text-red-400" : "text-emerald-400"}`}>
                            {tx.amount < 0 ? "−" : "+"}${Math.abs(tx.amount || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        <div className="card-lux p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="admin-withdraw-section">
          <div>
            <p className="text-sm text-white font-medium">Withdraw</p>
            <p className="text-[11px] text-[#64748B] mt-1">
              Internal financial tracking only — recording a withdrawal here does NOT trigger any bank transfer or payment gateway transaction.
              Available (all-time): <span className="text-[#D4AF37] font-mono" data-testid="withdraw-true-balance">${trueBalance.toFixed(2)}</span>
            </p>
          </div>
          <button
            onClick={() => setShowAdminWithdraw(true)}
            disabled={!trueWallet || trueBalance <= 0}
            className="btn-gold px-6 py-2.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="admin-withdraw-button"
          >
            <Wallet className="h-3.5 w-3.5" /> Withdraw
          </button>
        </div>

        <div className="card-lux overflow-hidden">
          <p className="px-5 pt-5 text-[10px] font-mono tracking-widest uppercase text-[#64748B]">IB Withdrawal Requests (manual bank transfer — then update status here)</p>
          <div className="divide-y divide-[#222530] mt-2" data-testid="withdrawals-list">
            {withdrawals.length === 0 ? (
              <p className="px-5 py-8 text-center text-[#64748B] text-sm" data-testid="withdrawals-empty">No withdrawal requests.</p>
            ) : (
              withdrawals.map((w) => (
                <div key={w.id} className="flex items-center gap-4 px-5 py-4 flex-wrap" data-testid={`withdrawal-row-${shortId(w.id)}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium">{w.ib_name} <span className="text-[10px] text-[#64748B] font-mono">{w.ib_uid}</span></p>
                    <p className="text-[11px] text-[#94A3B8] font-mono mt-0.5" data-testid={`withdrawal-bank-${shortId(w.id)}`}>
                      {w.full_name} · {w.bank_name} · {w.bank_account_number}
                    </p>
                    <p className="text-[10px] text-[#64748B] font-mono mt-0.5">
                      Requested {fmtMYT(w.created_at)}{w.decided_at ? ` · Decided ${fmtMYT(w.decided_at)}` : ""}
                    </p>
                    {w.reject_reason && <p className="text-[10px] text-red-400/90 mt-0.5">Reason: {w.reject_reason}</p>}
                  </div>
                  <p className="text-sm font-mono text-[#D4AF37]">${w.amount?.toFixed(2)}</p>
                  <StatusChip status={w.status} />
                  {w.status === "PENDING" && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => completeWithdrawal(w)} className="text-[11px] text-emerald-400 border border-emerald-400/30 hover:bg-emerald-400/10 rounded-lg px-2.5 py-1.5 transition-colors" data-testid={`withdrawal-complete-${shortId(w.id)}`}>
                        Mark Completed
                      </button>
                      <button onClick={() => { setRejectWdReason(""); setRejectWd(w); }} className="text-[11px] text-red-400 border border-red-400/30 hover:bg-red-400/10 rounded-lg px-2.5 py-1.5 transition-colors" data-testid={`withdrawal-reject-${shortId(w.id)}`}>
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showStartPeriod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowStartPeriod(false)} />
          <div className="relative card-lux w-full max-w-md p-6 sm:p-8 space-y-4" data-testid="start-period-modal">
            <p className="eyebrow-label">Start New Accounting Period</p>
            <h3 className="font-heading text-lg font-semibold text-white">Begin a fresh reporting period?</h3>
            <p className="text-xs text-[#94A3B8] leading-relaxed">
              This sets a reporting cutoff at the current time. The <span className="text-[#D4AF37]">Current Period</span> view will then show only transactions from now onward.
            </p>
            <p className="text-[11px] text-[#64748B] leading-relaxed">
              This is a reporting change only. No wallet records, balances, commissions, claims, or withdrawals are deleted or modified — all history stays available under the <span className="text-[#D4AF37]">Historical</span> view.
            </p>
            <div className="flex gap-3 pt-1">
              <button onClick={submitStartPeriod} disabled={busy} className="btn-gold flex-1 py-2.5 text-xs disabled:opacity-50" data-testid="start-period-confirm-button">
                {busy ? "Starting…" : "Confirm — Start New Period"}
              </button>
              <button onClick={() => setShowStartPeriod(false)} className="btn-gold-outline px-5 py-2.5 text-xs" data-testid="start-period-cancel-button">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showAdminWithdraw && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowAdminWithdraw(false)} />
          <div className="relative card-lux w-full max-w-sm p-6 space-y-4" data-testid="admin-withdraw-modal">
            <p className="eyebrow-label">Record Withdrawal</p>
            <p className="text-xs text-[#64748B]">Available: <span className="text-[#D4AF37] font-mono">${trueBalance.toFixed(2)}</span>. Internal accounting record only — no bank transfer occurs.</p>
            <div>
              <label className={labelCls}>Amount (USD) <span className="text-red-400">*</span></label>
              <input type="number" min="0.01" step="0.01" className={inputCls} value={adminWithdrawAmount} onChange={(e) => setAdminWithdrawAmount(e.target.value)} placeholder="500.00" data-testid="admin-withdraw-amount-input" />
            </div>
            <div>
              <label className={labelCls}>Note <span className="text-[#64748B]">(optional)</span></label>
              <input className={inputCls} value={adminWithdrawNote} onChange={(e) => setAdminWithdrawNote(e.target.value)} placeholder="e.g. September payout" data-testid="admin-withdraw-note-input" />
            </div>
            <div className="flex gap-3">
              <button onClick={submitAdminWithdraw} disabled={busy || !adminWithdrawAmount || Number(adminWithdrawAmount) <= 0} className="btn-gold flex-1 py-2.5 text-xs disabled:opacity-50" data-testid="admin-withdraw-confirm-button">
                {busy ? "Recording…" : "Confirm Withdrawal"}
              </button>
              <button onClick={() => setShowAdminWithdraw(false)} className="btn-gold-outline px-5 py-2.5 text-xs" data-testid="admin-withdraw-cancel-button">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {rejectWd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setRejectWd(null)} />
          <div className="relative card-lux w-full max-w-md p-6 sm:p-8 space-y-4" data-testid="wd-reject-modal">
            <p className="eyebrow-label">Reject Withdrawal</p>
            <h3 className="font-heading text-lg font-semibold text-white">${rejectWd.amount?.toFixed(2)} — {rejectWd.ib_name}</h3>
            <div>
              <label className={labelCls}>Rejection Reason <span className="text-red-400">*</span></label>
              <textarea className="input-lux min-h-[80px]" value={rejectWdReason} onChange={(e) => setRejectWdReason(e.target.value)} data-testid="wd-reject-reason-input" />
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={submitRejectWithdrawal} disabled={busy || rejectWdReason.trim().length < 2} className="flex-1 py-2.5 text-sm rounded-lg font-medium bg-red-500/90 hover:bg-red-500 text-white transition-colors disabled:opacity-50" data-testid="wd-reject-confirm-button">
                {busy ? "Submitting…" : "Confirm Rejection"}
              </button>
              <button onClick={() => setRejectWd(null)} className="btn-gold-outline px-5 py-2.5 text-sm" data-testid="wd-reject-cancel-button">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
